/**
 * Types for the Repository access page (ADR 029): a tenant's git routing rules, its own Git tokens, and the
 * "test a URL" result. Shapes mirror the orchestrator's `/git-routing/*` and `/git-credentials` responses.
 */

/** Whether the tenant has a token under a rule's credential. `Unknown` when the store could not say. */
export type GitCredentialStatus = 'Set' | 'Missing' | 'Unknown'

export interface GitRoutingRule {
  id: string
  /** One-based: rules are tried in this order and the first match wins. */
  position: number
  urlPattern: string
  provider: string
  /** `{provider}:{name}`. It may not have a token yet. */
  credentialName: string
  createdAt: string
  createdBy?: string | null
  updatedAt: string
  updatedBy?: string | null
  credentialStatus: GitCredentialStatus
}

export interface ListGitRoutingRulesResponse {
  rules: GitRoutingRule[]
}

/** A new rule (no id) or an edit of an existing one. */
export interface UpsertGitRoutingRuleRequest {
  id?: string
  urlPattern: string
  provider: string
  credentialName: string
}

export interface DeploymentGitRoute {
  position: number
  urlPattern: string
  provider: string
  credentialRef?: string | null
}

export interface DeploymentHostDefault {
  host: string
  provider: string
}

/** The operator's own rules and host defaults, read-only. */
export interface DeploymentGitRouting {
  rules: DeploymentGitRoute[]
  hostDefaults: DeploymentHostDefault[]
}

export type GitRouteSource = 'TenantRule' | 'DeploymentRule' | 'HostDefault'

export type GitRepositoryAccess =
  | 'Readable'
  | 'NotFound'
  | 'Unauthorized'
  | 'Forbidden'
  | 'NotConfigured'
  | 'StoreUnavailable'
  | 'ProviderError'

export interface GitRoutingTestResult {
  url: string
  resolved: boolean
  /** Why nothing matched, when `resolved` is false. */
  problem?: string | null
  source?: GitRouteSource | null
  rulePosition?: number | null
  pattern?: string | null
  provider?: string | null
  owner?: string | null
  repository?: string | null
  credentialRef?: string | null
  access?: GitRepositoryAccess | null
  providerStatus?: number | null
  /** The provider's own words, already scrubbed of the token. */
  detail?: string | null
}

/** One of the tenant's Git tokens. Never its value. */
export interface GitCredential {
  name: string
  provider: string
  createdAt: string
  updatedAt: string
  setBy?: string | null
  lastUsedAt?: string | null
}

export interface ListGitCredentialsResponse {
  /** False when this deployment has no credential store, so tokens cannot be managed here. */
  available: boolean
  credentials: GitCredential[]
}

/** The providers a rule can route to, in the order the select lists them. */
export const GIT_PROVIDER_OPTIONS = [
  { value: 'github', label: 'GitHub' },
  { value: 'gitea', label: 'Gitea' },
  { value: 'ado', label: 'Azure DevOps' },
] as const

export type GitProvider = (typeof GIT_PROVIDER_OPTIONS)[number]['value']
